import { useState, useEffect, useCallback } from 'react';
import { api, PendingCession } from '../api/client';
import RefuseModal from '../components/RefuseModal';
import ConfirmModal from '../components/ConfirmModal';
import ProofModal from '../components/ProofModal';
import ToastStack from '../components/Toast';
import { useToast } from '../hooks/useToast';

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-CA', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

type Action =
  | { type: 'approve'; cession: PendingCession }
  | { type: 'refuse';  cession: PendingCession };

export default function Cessions({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [cessions, setCessions] = useState<PendingCession[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [pending, setPending] = useState<Action | null>(null);
  const [proofPreview, setProofPreview] = useState<PendingCession | null>(
    null,
  );
  const { toasts, push, dismiss } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api
      .getPendingCessions()
      .then((r) => {
        setCessions(r.data);
        onCountChange?.(r.data.length);
      })
      .catch(() => push('Impossible de charger les cessions.', 'error'))
      .finally(() => setLoading(false));
  }, [onCountChange, push]);

  useEffect(() => { load(); }, [load]);

  async function approve(cession: PendingCession) {
    setActionLoading(cession.id);
    try {
      await api.approveCession(cession.id);
      push(`"${cession.title}" approuvée — cession maintenant active.`, 'success');
      setCessions((prev) => prev.filter((c) => c.id !== cession.id));
      onCountChange?.(cessions.length - 1);
    } catch (err: unknown) {
      push(err instanceof Error ? err.message : 'Erreur.', 'error');
    } finally {
      setActionLoading(null);
      setPending(null);
    }
  }

  async function refuse(cession: PendingCession, reason: string) {
    setActionLoading(cession.id);
    try {
      await api.refuseCession(cession.id, reason);
      push(`"${cession.title}" refusée.`, 'success');
      setCessions((prev) => prev.filter((c) => c.id !== cession.id));
      onCountChange?.(cessions.length - 1);
    } catch (err: unknown) {
      push(err instanceof Error ? err.message : 'Erreur.', 'error');
    } finally {
      setActionLoading(null);
      setPending(null);
    }
  }

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <>
      <ToastStack toasts={toasts} dismiss={dismiss} />

      <div className="section-head">
        <div>
          <div className="section-title">Cessions en attente de vérification</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            Preuves soumises par les hôtes — vérifiez le document et approuvez ou refusez.
          </div>
        </div>
        <div className="section-count">{cessions.length} cession{cessions.length !== 1 ? 's' : ''}</div>
      </div>

      {cessions.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">✓</div>
          <div className="empty-title">Aucune cession en attente</div>
          <div className="empty-sub">Toutes les cessions ont été traitées.</div>
        </div>
      ) : (
        <div className="offer-grid">
          {cessions.map((cession) => (
            <CessionCard
              key={cession.id}
              cession={cession}
              busy={actionLoading === cession.id}
              onApprove={() => setPending({ type: 'approve', cession })}
              onRefuse={() => setPending({ type: 'refuse', cession })}
              onViewProof={() => setProofPreview(cession)}
            />
          ))}
        </div>
      )}

      {pending?.type === 'approve' && (
        <ConfirmModal
          title="Approuver la cession"
          description={`"${pending.cession.title}" deviendra immédiatement active sur le feed live.`}
          confirmLabel="Approuver"
          confirmVariant="success"
          onConfirm={() => approve(pending.cession)}
          onCancel={() => setPending(null)}
          loading={actionLoading === pending.cession.id}
        />
      )}

      {pending?.type === 'refuse' && (
        <RefuseModal
          offerTitle={pending.cession.title}
          onConfirm={(reason) => refuse(pending.cession, reason)}
          onCancel={() => setPending(null)}
          loading={actionLoading === pending.cession.id}
        />
      )}

      {proofPreview?.proof_document_url && (
        <ProofModal
          title={`Preuve — ${proofPreview.title}`}
          imageUrl={proofPreview.proof_document_url}
          onClose={() => setProofPreview(null)}
        />
      )}
    </>
  );
}

interface CessionCardProps {
  cession: PendingCession;
  busy: boolean;
  onApprove: () => void;
  onRefuse: () => void;
  onViewProof: () => void;
}

function CessionCard({
  cession,
  busy,
  onApprove,
  onRefuse,
  onViewProof,
}: CessionCardProps) {
  const discount = cession.proof_original_price > 0
    ? Math.round((1 - cession.reference_price / cession.proof_original_price) * 100)
    : 0;

  return (
    <div className="offer-card">
      <div className="offer-body">
        <div className="offer-title">{cession.title}</div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
          <div className="offer-price">{formatPrice(cession.reference_price)}</div>
          {discount > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: 'line-through' }}>
              {formatPrice(cession.proof_original_price)}
            </div>
          )}
          {discount > 0 && (
            <div style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>
              -{discount}%
            </div>
          )}
        </div>

        <div className="offer-meta">
          <div className="offer-meta-row">
            <span className="icon">◎</span>
            {cession.location_label}
          </div>
          <div className="offer-meta-row">
            <span className="icon">◷</span>
            Séjour {formatDate(cession.starts_at)} → {formatDate(cession.checkout_at)}
          </div>
          <div className="offer-meta-row">
            <span className="icon">◷</span>
            Réservable jusqu’au {formatDate(cession.expires_at)}
          </div>
          <div className="offer-meta-row">
            <span className="icon">◷</span>
            Soumis {formatDate(cession.created_at)}
          </div>
        </div>

        {cession.description && (
          <div className="offer-desc">{cession.description}</div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {cession.proof_document_url ? (
            <button
              type="button"
              className="btn"
              onClick={onViewProof}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                padding: '5px 10px',
                background: 'var(--surface-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text)',
              }}
            >
              ↗ Voir la preuve
            </button>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--danger)', alignSelf: 'center' }}>
              ⚠ Aucun document de preuve
            </div>
          )}

          {cession.reference_url && (
            <a
              href={cession.reference_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                padding: '5px 10px',
                background: 'var(--surface-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text)',
                textDecoration: 'none',
              }}
            >
              ↗ Annonce d'origine
            </a>
          )}
        </div>

        <div className="offer-host">
          <span style={{ color: 'var(--text-dim)', fontSize: 10, marginRight: 4 }}>Hôte</span>
          {cession.host_id}
        </div>
      </div>

      <div className="offer-actions">
        <button
          className="btn btn-success"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={busy}
          onClick={onApprove}
        >
          {busy ? <span className="spinner" /> : '✓'}
          Approuver
        </button>
        <button
          className="btn btn-danger"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={busy}
          onClick={onRefuse}
        >
          ✕ Refuser
        </button>
      </div>
    </div>
  );
}
